import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useNavigate, useParams } from 'react-router-dom';

import axios, { AxiosError, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';

import { EVENT } from '../../globals';

import { useApp } from '../_libs/hooks/useApp';
import { useBrand } from '../_libs/hooks/useBrand';

import ErrorBoundary from '../organisms/ErrorBoundary';

import './index.scss';

const IndexPage = (): JSX.Element => {
  const { userId, reportData, setReportData, setRiskDriverLimit, setAppTitle } = useApp();
  const { appBrandTitle, primaryBrand, isCoBranded, coBrand, isValidBrand, fetchCoBrand, setCoBrandUrl } = useBrand();
  const { secondaryBrand } = useParams();
  const navigate = useNavigate();

  const [pageViewed, setPageViewed] = useState(false);
  const [error, setError] = useState(null);

  // Check and set secondary branding for dashboard and materials
  useEffect(() => {
    setRiskDriverLimit(3); // Reset limit
    fetchCoBrand(secondaryBrand);
    if (secondaryBrand == 'lb') {
      setCoBrandUrl('lb');
      setRiskDriverLimit(6);
    }
  }, []);

  useEffect(() => {
    if (pageViewed === false && userId) {
      axios.post('/create_event/', {
        eventCode: EVENT.VisitSite,
        userId,
        key: 'pathname',
        value: document.location.pathname,
      });
      setPageViewed(true);
    }
  }, [pageViewed, userId]);
  
  // Set page UI colors based on valid brand data
  useEffect(() => {
    let _brand = null;
    if (isCoBranded && isValidBrand(coBrand)) {
      _brand = coBrand;
      // FIXME: 506 is hard-coded omission of FLAACOs
      if (secondaryBrand !== 'flaacos') {
        setRiskDriverLimit(6);
      }
    } else if (isValidBrand(primaryBrand)) {
      _brand = primaryBrand;
    }
    if (_brand) {
      document.body.style.backgroundColor = _brand.colors.dark;
      document.body.style.color = _brand.colors.light;
    }
    document.getElementById('body-gradient').style.opacity = '0';
    document.getElementById('js-bg').style.opacity = '1';
  }, [primaryBrand, coBrand]);

  // Update document.title
  useEffect(() => { setAppTitle(appBrandTitle); }, [appBrandTitle]);

  /**
   * Reports are destroyed each time a user loads /
   * create a new report when 'Get Started' is clicked
   */
  function handleGetStarted () {
    const isNewSession = reportData === null;
    if (isNewSession) {
      axios
        .post('/create_report/', {
          coBrandId: coBrand.id,
          userId,
        })
        .then((res: AxiosResponse) => {
          const data = res.data;
          if (data.status === 'OK') {
            setError(null);
            setReportData(data.report);
            navigate('/organization/');
          }
        })
        .catch((err: AxiosError) => {
          Sentry.captureException(err);
          setError(err);
        }); 
    } else {
      navigate('/organization/');
    }
  }

  return (
    <ErrorBoundary>
      <div className="content-container">
        <div className="content">
          <Container>
            <Row>
              <Col md={7}>
                <h1>
                  Your health equity strategy in a box.
                </h1>
                <p className="f-large">
                  Lightbeam Health Solutions is proud to offer Radian: <span className="no-wrap">a&nbsp;no-cost</span>, Artificial Intelligence-driven health equity tool. We built Radian to support any healthcare organization intent on advancing health equity or overcoming barriers to social determinants of health.
                </p>
                <button className="button" type="button" onClick={handleGetStarted}>Build Your Toolkit</button>
                {/* TODO: Create <Error /> component to print out and log errors in a uniform way. */}
                { error && <div>An error has occurred. Try clicking again.</div> }
              </Col>
            </Row>
          </Container>
        </div>
        <div className="content">
          <Container>
            <Row gutterWidth={144}>
              <Col md={6}>
                <h3>
                  View Geography-Specific,
                  <br />
                  <em>AI-Powered Insights</em>
                </h3>
                <img className="mb-4" alt="Map data between a rural and urban environment" src="/static/img/radian-data.png" />
                <ul>
                  <li>Learn where to focus resources like mobile care delivery or screenings</li>
                  <li>Identify community vulnerabilities to support activities like writing grant applications, establishing new clinics, or developing targeted messaging</li>
                  <li>View opportunities to align current work with AI-identified areas for impact</li>
                </ul>
              </Col>
              <Col md={6}>
                <h3>
                  Generate Customized,
                  <br />
                  <em>Community-Aligned Materials</em>
                </h3>
                <img className="mb-4" alt="Example of custom materials" src="/static/img/radian-materials.png" />
                <ul>
                  <li>Create deployable educational resources with your preferred organization or clinic name, brand color, and logo</li>
                  <li>Use AI-generated faces to match the race and age of your population so that the communications achieve maximum identity alignment</li>
                  <li>Share evidence-based literature on potential health equity barriers with your clinical staff</li>
                </ul>
              </Col>
            </Row>
          </Container>
        </div>
        <div className="content">
          <Container>
            <h2>
              Take the first step toward health equity today.<br />
              <strong>Everyone will benefit tomorrow.</strong>
            </h2>
            <p>
              With initiatives such as ACO REACH and the Enhanced Oncology Model, CMS emphasizes the importance of a robust SDOH and Health Equity strategy. Between 2023 and 2025, leading providers leveraging Lightbeam’s Health Equity offerings will benefit their communities and themselves.
            </p>
            <Row gutterWidth={72} style={{ paddingTop: '2rem' }}>
              <Col lg={4}>
                <h3 className="t-transform-none">
                  Analytics for
                  <br />
                  <em>ACO REACH and Health&nbsp;Equity</em>
                </h3>
                <img src="/static/img/lb-aco-reach.png" alt="Abstract reference to different data sources going into Lightbeam" className="mb-4" />
                <p>
                  Leverage HIE, EHR, Claims, ADT, and other data sources to provide the most accurate analytics for the communities you serve.
                </p>
              </Col>
              <Col lg={4}>
                <h3 className="t-transform-none">
                  Deviceless RPM for
                  <br />
                  <em>Social Determinants of&nbsp;Health</em>
                </h3>
                <img src="/static/img/cs-sdoh.png" alt="Illustration of a phone number with text messages discussing SDoH needs" className="mb-4" />
                <p>
                  Collect patient- or member-reported outcomes to track potential barriers to health equity so your LSW or Care Management teammates can reach out at the right time to help.
                </p>
              </Col>
              <Col lg={4}>
                <h3 className="t-transform-none">
                  Health Equity AI for
                  <br />
                  <em>Individual-level Health&nbsp;Equity</em>
                </h3>
                <img src="/static/img/jv-individual.png" alt="An individual surrounded by different health equity concerns with one highlighted in red, meaning high-risk" className="mb-4" />
                <p>
                  Employ patient- or member-specific predictive models to uncover risk factors and review prescriptive recommendations for everyone you serve.
                </p>
              </Col>
            </Row>
          </Container>
        </div>
        <div className="content">
          <Container>
            <Row>
              <Col sm={12}>
                <h3>
                  Interested in any of the health equity solutions above?
                </h3>
                <a className="button td-none outline" href="https://lightbeamhealth.com/request-a-demo/">Request a Demo</a>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div className="footer">
        <Container>
          <Row align="center" style={{ height: '3rem' }}>
            <Col>
              © 2022–{new Date().getFullYear()} Lightbeam Health Solutions.
            </Col>
            <Col style={{ textAlign: 'right' }}>
              <a href="https://lightbeamhealth.com/privacy-policy/">Privacy Policy</a>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="navigation">
        <Container>
          <Row justify="between" align="center" style={{ height: '3rem' }}>
            <Col>
              <div className="logo">
                { appBrandTitle }
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ErrorBoundary>
  );
};

export default IndexPage;
